import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CampSignIn } from "@/components/camp/CampSignIn";
import { supabase } from "@/integrations/supabase/client";
import { AgeQuestion } from "@/components/entry/AgeQuestion";
import { useAgeGatedAction } from "@/hooks/useAgeFloor";
import { useClientValue } from "@/hooks/useClientValue";
import { useHydrated } from "@/hooks/useHydrated";

import {
  startAppleSignIn,
  startGoogleSignIn,
  startMicrosoftSignIn,
} from "@/components/auth/googleSignIn";
import { signInFailureKey } from "@/components/auth/signInFailure";
import { landingUrlFor, type EntryIntent } from "@/engine/state/entryIntent";
import {
  readLastSignIn,
  rememberSignIn,
  type SignInMethod,
} from "@/engine/state/lastSignIn";
import { trackEvent } from "@/engine/diagnostics/analytics";

type OAuthProvider = "google" | "apple" | "microsoft";

/** The provider names are trademarks and stay as they are spelled. */
const PROVIDER_NAMES: Record<OAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
  microsoft: "Microsoft",
};

const STARTERS: Record<
  OAuthProvider,
  (intent: EntryIntent | null, returnTo?: string) => Promise<boolean>
> = {
  google: startGoogleSignIn,
  apple: startAppleSignIn,
  microsoft: startMicrosoftSignIn,
};

/**
 * The handles the application's tests already hold. Google's keeps the name it
 * had when it was the one way to create a room.
 */
const TEST_IDS = {
  root: "entry-needs-auth",
  google: "entry-sign-in-to-create",
  apple: "entry-sign-in-apple",
  microsoft: "entry-sign-in-microsoft",
  email: "auth-email",
  sendLink: "auth-send-link",
  sent: "auth-sent",
  error: "auth-error",
};

function landingUrlForIntent(intent: EntryIntent | null): string | undefined {
  if (typeof window === "undefined") return undefined;
  if (!intent) return `${window.location.origin}/home`;
  return landingUrlFor(window.location.origin, intent);
}

/** What signing in is waiting on and what it said, and the ways to start it. */
function useSignIn(intent: EntryIntent | null, returnTo: string | undefined) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState<OAuthProvider | "email" | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const landing = returnTo ?? landingUrlForIntent(intent);

  const sendLink = async () => {
    setPending("email");
    setError(undefined);
    const { error: failure } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: landing },
    });
    setPending(null);
    if (failure) {
      // The two failures visitors actually hit are both temporary: the hourly
      // cap on sign-in emails, and the mailer taking too long to answer. A raw
      // provider string reads like the address was rejected, so say what it is.
      setError(t(signInFailureKey(failure)));
      return;
    }
    rememberSignIn("email");
    trackEvent("sign_in_link_requested");
    setSent(true);
  };

  const startProvider = async (provider: OAuthProvider) => {
    setPending(provider);
    setError(undefined);
    rememberSignIn(provider);
    const started = await STARTERS[provider](intent, returnTo);
    if (started) return;
    setPending(null);
    setError(
      t("account.providerFailed", { provider: PROVIDER_NAMES[provider] }),
    );
  };

  return {
    email,
    setEmail,
    sent,
    reset: () => setSent(false),
    pending,
    error,
    sendLink,
    startProvider,
  };
}

export interface SignInProps {
  /**
   * What the visitor was about to do, carried through the round trip.
   * When `null`, signing in simply returns them to the home page.
   */
  intent: EntryIntent | null;
  /** Why an account is being asked for. */
  reason: string;
  /**
   * Where the round trip comes back to, when an intent cannot say it. The
   * OAuth consent page is the case: it has to be returned to at its exact
   * address, because the authorization it is deciding lives in that address.
   */
  returnTo?: string;
  onBack?: () => void;
  /** Carrying on without an account, where that is allowed. */
  onSkip?: () => void;
}

/**
 * Every way in, wherever the visitor is asked: the brandbook's sign-in with the
 * catalogue's words, so the providers and the emailed link behave the same on
 * every page and the round trip comes back carrying the same intent.
 */
export function SignIn({
  intent,
  reason,
  returnTo,
  onBack,
  onSkip,
}: SignInProps) {
  const { t } = useTranslation();
  const signIn = useSignIn(intent, returnTo);
  const age = useAgeGatedAction();
  // Nothing here can act before React has attached, so the controls say so
  // rather than swallowing a press and looking broken.
  const live = useHydrated();
  // Which door worked last time, so it can be offered first rather than found
  // again. Only known after hydration — the server cannot know this visitor.
  const lastUsed = useClientValue<SignInMethod | null>(readLastSignIn, null);

  return (
    <fieldset disabled={!live} className="contents">
      <p className="mb-3 text-center text-xs text-muted-foreground">{reason}</p>
      <CampSignIn
        phase={signIn.sent ? "sent" : "choose"}
        pending={signIn.pending}
        email={signIn.email}
        error={signIn.error}
        onEmailChange={signIn.setEmail}
        onEmailSubmit={() => age.run(() => void signIn.sendLink())}
        onProvider={(provider) =>
          age.run(() => void signIn.startProvider(provider))
        }
        onOtherAddress={signIn.reset}
        onBack={onBack}
        onSkip={onSkip}
        lastUsed={lastUsed}
        testIds={TEST_IDS}
        copy={{
          provider: (provider) => t("account.continueWith", { provider }),
          emailDivider: t("account.or"),
          emailLabel: t("account.emailLabel"),
          emailPlaceholder: t("account.emailPlaceholder"),
          sendLink: t("account.emailMeALink"),
          sentTitle: t("account.sentTitle"),
          sentBody: (address) =>
            t(intent ? "account.sentBodyIntoRoom" : "account.sentBody", {
              email: address.trim(),
            }),
          // Never drawn: the link is opened from the email, not from here.
          openLink: "",
          otherAddress: t("account.differentAddress"),
          back: t("action.back"),
          skip: t("account.carryOn"),
          lastUsed: t("account.lastUsed"),
        }}
      />
      <AgeQuestion {...age.question} />
    </fieldset>
  );
}
